import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, User, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

function formatError(detail) {
  if (!detail) return 'Something went wrong';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e.msg || JSON.stringify(e)).join('. ');
  return String(detail);
}

export default function Auth() {
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (mode === 'login') {
        const user = await login(email, password);
        navigate(user.role === 'admin' ? '/admin' : '/');
      } else {
        await register(email, password, name);
        navigate('/');
      }
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="store-page flex items-center justify-center relative px-4">
      <Link to="/" className="absolute top-6 left-6 flex items-center gap-2 text-ink/55 hover:text-ink transition-colors z-10">
        <ArrowLeft className="w-4 h-4" /> Back to Store
      </Link>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="glass-card rounded-3xl p-8 w-full max-w-md relative z-10"
      >
        <div className="text-center mb-8">
          <div className="w-12 h-12 bg-honey/10 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-honey/20">
            <User className="w-6 h-6 text-honey-700" />
          </div>
          <h1 className="text-2xl font-black text-ink" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {mode === 'login' ? 'Welcome Back' : 'Create Account'}
          </h1>
          <p className="text-ink/55 text-sm mt-1">
            {mode === 'login' ? 'Sign in to your account' : 'Join our store today'}
          </p>
        </div>

        <div className="flex gap-2 mb-6 bg-ink/[0.04] rounded-xl p-1">
          {['login', 'register'].map(m => (
            <button key={m} onClick={() => setMode(m)}
              data-testid={`auth-tab-${m}`}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all capitalize ${mode === m ? 'bg-honey text-black' : 'text-ink/55 hover:text-ink'}`}>
              {m === 'login' ? 'Sign In' : 'Register'}
            </button>
          ))}
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-600 text-sm" data-testid="auth-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === 'register' && (
            <div className="relative">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-ink/45" />
              <input
                type="text" value={name} onChange={e => setName(e.target.value)}
                placeholder="Full Name" required
                data-testid="auth-name-input"
                className="w-full bg-ink/[0.04] border border-ink/10 rounded-xl pl-12 pr-4 py-3 text-ink placeholder-ink/40 focus:outline-none focus:border-honey transition-colors"
              />
            </div>
          )}
          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-ink/45" />
            <input
              type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="Email address" required
              data-testid="auth-email-input"
              className="w-full bg-ink/[0.04] border border-ink/10 rounded-xl pl-12 pr-4 py-3 text-ink placeholder-ink/40 focus:outline-none focus:border-honey transition-colors"
            />
          </div>
          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-ink/45" />
            <input
              type={showPass ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)}
              placeholder="Password" required
              data-testid="auth-password-input"
              className="w-full bg-ink/[0.04] border border-ink/10 rounded-xl pl-12 pr-12 py-3 text-ink placeholder-ink/40 focus:outline-none focus:border-honey transition-colors"
            />
            <button type="button" onClick={() => setShowPass(!showPass)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-ink/45 hover:text-ink/70">
              {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <button type="submit" disabled={loading}
            data-testid="auth-submit-btn"
            className="w-full btn-primary py-3 text-center font-bold disabled:opacity-50 disabled:cursor-not-allowed">
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                {mode === 'login' ? 'Signing in...' : 'Creating account...'}
              </span>
            ) : mode === 'login' ? 'Sign In' : 'Create Account'}
          </button>
        </form>

        <p className="text-center text-ink/55 text-sm mt-6">
          {mode === 'login' ? "Don't have an account? " : "Already have an account? "}
          <button onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
            className="text-honey-700 hover:underline">
            {mode === 'login' ? 'Register' : 'Sign In'}
          </button>
        </p>
      </motion.div>
    </div>
  );
}
